import { useState, useEffect, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';

/**
 * Custom hook to stream attachment scanning status using Server-Sent Events (SSE),
 * with HTTP polling fallback when SSE stalls or fails.
 * @param {string} fileKey - The S3 file key to monitor
 * @param {boolean} enabled - Whether SSE connection should be active
 * @returns {Object} - Hook state including isScanningComplete and isPolling
 */
export const useScanningStatusSSE = (fileKey, enabled = true) => {
  const [scannedStatus, setScannedStatus] = useState(null);
  const [attachment, setAttachment] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  /** True while HTTP scanStatus polling fallback is active (after SSE stall/close). */
  const [isHttpPollingFallback, setIsHttpPollingFallback] = useState(false);

  const eventSourceRef = useRef(null);
  const isMountedRef = useRef(true);
  const pollingIntervalRef = useRef(null);
  const watchdogTimerRef = useRef(null);
  const noStatusTimerRef = useRef(null);
  const scanHardTimeoutRef = useRef(null);
  const scannedStatusRef = useRef(null);
  const hasReceivedStatusEventRef = useRef(false);
  const pollingFallbackStartedRef = useRef(false);

  const projectId = useSelector((state) => state.projects.selectedProject.id);

  const FINAL_STATUSES = ['CLEAN', 'INFECTED', 'ERROR'];
  const NO_STATUS_FALLBACK_MS = 15000; // fall back only if SSE never sends a status payload
  const HARD_TIMEOUT_MS = 180000; // prevent infinite spinner (3 minutes)

  useEffect(() => {
    scannedStatusRef.current = scannedStatus;
  }, [scannedStatus]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const clearPollingInterval = () => {
    if (pollingIntervalRef.current != null) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  };

  const clearWatchdog = () => {
    if (watchdogTimerRef.current != null) {
      clearTimeout(watchdogTimerRef.current);
      watchdogTimerRef.current = null;
    }
  };

  const clearNoStatusTimer = () => {
    if (noStatusTimerRef.current != null) {
      clearTimeout(noStatusTimerRef.current);
      noStatusTimerRef.current = null;
    }
  };

  const clearHardTimeout = () => {
    if (scanHardTimeoutRef.current != null) {
      clearTimeout(scanHardTimeoutRef.current);
      scanHardTimeoutRef.current = null;
    }
  };

  const startPollingFallback = useCallback(() => {
    if (pollingFallbackStartedRef.current) {
      return;
    }

    clearPollingInterval();

    if (!projectId || !fileKey) {
      return;
    }

    const status = scannedStatusRef.current;
    if (status && FINAL_STATUSES.includes(status)) {
      return;
    }

    pollingFallbackStartedRef.current = true;

    const apiUrl = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
    const encodedFileKey = encodeURIComponent(fileKey);
    const url = `${apiUrl}/${projectId}/attachment/scanStatus?fileKey=${encodedFileKey}`;

    if (isMountedRef.current) {
      setIsHttpPollingFallback(true);
    }

    const pollOnce = async () => {
      if (!isMountedRef.current) return;

      const current = scannedStatusRef.current;
      if (current && FINAL_STATUSES.includes(current)) {
        clearPollingInterval();
        if (isMountedRef.current) setIsHttpPollingFallback(false);
        return;
      }

      try {
        const res = await fetch(url, { credentials: 'include' });
        const json = await res.json();
        const scanStatus = json?.data?.scanStatus;

        if (scanStatus && FINAL_STATUSES.includes(scanStatus)) {
          clearPollingInterval();
          if (!isMountedRef.current) return;
          setIsHttpPollingFallback(false);
          setScannedStatus(scanStatus);
          setIsLoading(false);
          clearWatchdog();
          clearNoStatusTimer();
          clearHardTimeout();
          if (eventSourceRef.current) {
            eventSourceRef.current.close();
            eventSourceRef.current = null;
          }
          setIsConnected(false);
        }
      } catch (e) {
        console.error('Scan status poll failed', e);
      }
    };

    pollOnce();
    pollingIntervalRef.current = setInterval(pollOnce, 6000);
  }, [projectId, fileKey]);

  useEffect(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    clearPollingInterval();
    clearWatchdog();
    clearNoStatusTimer();
    clearHardTimeout();
    if (isMountedRef.current) {
      setIsHttpPollingFallback(false);
    }
    hasReceivedStatusEventRef.current = false;
    pollingFallbackStartedRef.current = false;

    if (!enabled || !fileKey || !projectId) {
      setIsLoading(false);
      setIsConnected(false);
      return;
    }

    setIsLoading(true);
    setError(null);
    setIsConnected(false);

    const resetWatchdog = () => {
      clearWatchdog();
      watchdogTimerRef.current = setTimeout(() => {
        if (!isMountedRef.current) return;
        if (eventSourceRef.current) {
          eventSourceRef.current.close();
          eventSourceRef.current = null;
        }
        setIsConnected(false);
        const s = scannedStatusRef.current;
        if (!s || !FINAL_STATUSES.includes(s)) {
          startPollingFallback();
        }
      }, 35000);
    };

    const scheduleNoStatusFallback = () => {
      clearNoStatusTimer();
      noStatusTimerRef.current = setTimeout(() => {
        if (!isMountedRef.current) return;
        // Only fall back when SSE never delivered a status payload (null = still scanning is valid).
        if (!hasReceivedStatusEventRef.current) {
          if (eventSourceRef.current) {
            eventSourceRef.current.close();
            eventSourceRef.current = null;
          }
          setIsConnected(false);
          startPollingFallback();
        }
      }, NO_STATUS_FALLBACK_MS);
    };

    const handleStatusPayload = (scanned_status) => {
      hasReceivedStatusEventRef.current = true;
      clearNoStatusTimer();
      if (scanned_status !== scannedStatusRef.current) {
        setScannedStatus(scanned_status);
      }
      setIsLoading(false);
      resetWatchdog();
      if (scanned_status && FINAL_STATUSES.includes(scanned_status)) {
        clearWatchdog();
        clearPollingInterval();
        clearNoStatusTimer();
        clearHardTimeout();
        if (isMountedRef.current) setIsHttpPollingFallback(false);
      }
    };

    try {
      const encodedFileKey = encodeURIComponent(fileKey);
      const apiUrl = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
      const sseUrl = `${apiUrl}/${projectId}/attachment/streamStatus?fileKey=${encodedFileKey}`;

      const eventSource = new EventSource(sseUrl, {
        withCredentials: true
      });
      eventSourceRef.current = eventSource;

      eventSource.onopen = () => {
        if (!isMountedRef.current) return;
        setIsConnected(true);
        resetWatchdog();
        scheduleNoStatusFallback();
        clearHardTimeout();
        scanHardTimeoutRef.current = setTimeout(() => {
          if (!isMountedRef.current) return;
          const s = scannedStatusRef.current;
          if (!s || !FINAL_STATUSES.includes(s)) {
            if (eventSourceRef.current) {
              eventSourceRef.current.close();
              eventSourceRef.current = null;
            }
            clearPollingInterval();
            setIsConnected(false);
            setIsHttpPollingFallback(false);
            setIsLoading(false);
            setError('Scanning timed out. Please try uploading again.');
          }
        }, HARD_TIMEOUT_MS);
      };

      eventSource.onmessage = (event) => {
        if (!isMountedRef.current) return;

        try {
          if (event.data && event.data.trim().startsWith(':')) {
            return;
          }

          const data = JSON.parse(event.data);

          if (data.type === 'keepalive') {
            // keepalive should not extend the "no status" grace window
            resetWatchdog();
            return;
          }

          if (data.scanned_status !== undefined) {
            handleStatusPayload(data.scanned_status);
          }

          if (data.attachment) {
            setAttachment(data.attachment);
          }
        } catch (parseError) {
          console.error('Error parsing SSE data:', parseError, 'Raw data:', event.data);
        }
      };

      eventSource.addEventListener('complete', (event) => {
        if (!isMountedRef.current) return;

        clearWatchdog();
        clearPollingInterval();
        clearNoStatusTimer();
        clearHardTimeout();
        if (isMountedRef.current) setIsHttpPollingFallback(false);

        try {
          const data = JSON.parse(event.data);
          if (data.scanned_status !== undefined) {
            handleStatusPayload(data.scanned_status);
          }
          if (data.attachment) {
            setAttachment(data.attachment);
          }
        } catch (parseError) {
          console.error('Error parsing complete event:', parseError);
        }

        if (eventSourceRef.current) {
          eventSourceRef.current.close();
          eventSourceRef.current = null;
          setIsConnected(false);
        }
      });

      eventSource.addEventListener('timeout', () => {
        if (!isMountedRef.current) return;
        if (eventSourceRef.current) {
          eventSourceRef.current.close();
          eventSourceRef.current = null;
        }
        setIsConnected(false);
        const s = scannedStatusRef.current;
        if (!s || !FINAL_STATUSES.includes(s)) {
          startPollingFallback();
        }
      });

      eventSource.addEventListener('scan_error', (event) => {
        if (!isMountedRef.current) return;
        try {
          const data = JSON.parse(event.data);
          setError(data.error || 'Error receiving status update');
        } catch {
          setError('Error receiving status update');
        }
      });

      eventSource.onerror = () => {
        if (!isMountedRef.current) return;

        if (eventSource.readyState === EventSource.CLOSED) {
          setIsConnected(false);

          const s = scannedStatusRef.current;
          if (!s || !FINAL_STATUSES.includes(s)) {
            if (eventSourceRef.current) {
              eventSourceRef.current.close();
              eventSourceRef.current = null;
            }
            // Poll only when SSE never connected; after timeout event handles active streams.
            if (!hasReceivedStatusEventRef.current) {
              startPollingFallback();
            }
            return;
          }

          setIsLoading(false);
          if (eventSourceRef.current) {
            eventSourceRef.current.close();
            eventSourceRef.current = null;
          }
        } else if (eventSource.readyState === EventSource.CONNECTING) {
          setIsConnected(false);
        }
      };
    } catch (err) {
      if (!isMountedRef.current) return;
      console.error('Error creating SSE connection:', err);
      setError(err.message || 'Failed to connect to status stream');
      setIsLoading(false);
      setIsConnected(false);
      startPollingFallback();
    }

    return () => {
      clearPollingInterval();
      clearWatchdog();
      clearNoStatusTimer();
      clearHardTimeout();
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      setIsConnected(false);
      setIsHttpPollingFallback(false);
    };
  }, [fileKey, enabled, projectId, startPollingFallback]);

  const isSseOrLoadingScan =
    enabled &&
    fileKey &&
    (isLoading || isConnected) &&
    (!scannedStatus || !FINAL_STATUSES.includes(scannedStatus));

  return {
    scannedStatus,
    attachment,
    isLoading,
    error,
    isConnected,
    isScanningComplete: scannedStatus && FINAL_STATUSES.includes(scannedStatus),
    /** True while waiting on SSE/loading or while HTTP polling fallback is active (existing UI uses this). */
    isPolling: isHttpPollingFallback || isSseOrLoadingScan
  };
};
